import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface TimeRangePickerProps {
  value: string
  onChange: (value: string) => void
  minTime?: string
  maxTime?: string
  disabled?: boolean
  className?: string
  startAdornment?: string
  error?: boolean
}

export function TimeRangePicker({
  value,
  onChange,
  minTime,
  maxTime,
  disabled = false,
  className,
  startAdornment,
  error = false,
}: TimeRangePickerProps) {
  const generateTimeOptions = () => {
    const options: string[] = []
    const minTimeMinutes = minTime ? parseTime(minTime) : 0
    const maxTimeMinutes = maxTime ? parseTime(maxTime) : 24 * 60

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        const timeMinutes = hour * 60 + minute

        if (timeMinutes > minTimeMinutes && timeMinutes < maxTimeMinutes) {
          options.push(timeString)
        }
      }
    }

    return options
  }

  const parseTime = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    const displayMinutes = minutes.toString().padStart(2, '0')
    return `${displayHours}:${displayMinutes} ${period}`
  }

  const timeOptions = generateTimeOptions()

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={`${className} ${error ? 'bg-red-50 border-red-300 text-red-700' : ''}`}
      >
        <SelectValue placeholder="Select time">
          {startAdornment && (
            <span
              className={`text-xs mr-1 ${error ? 'text-red-900' : 'text-muted-foreground'}`}
            >
              {startAdornment}
            </span>
          )}
          <span className={`w-16 text-left ${error ? 'text-red-700' : ''}`}>
            {value && formatTime(value)}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-60">
        {timeOptions.map(time => {
          const timeMinutes = parseTime(time)
          const minTimeMinutes = minTime ? parseTime(minTime) : 0
          const maxTimeMinutes = maxTime ? parseTime(maxTime) : 24 * 60
          const isDisabled =
            timeMinutes <= minTimeMinutes || timeMinutes >= maxTimeMinutes

          return (
            <SelectItem key={time} value={time} disabled={isDisabled}>
              {formatTime(time)}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
